import React from "react";
import PropTypes from "prop-types";
import { Empty, Icon, message } from "antd";
import { generateCreditBrief } from "services/case-service";
import "styles/credit-brief-tab.css";

/**
 * Credit Brief tab — full-page inline viewer for the case's rendered PDF.
 *
 * Same backend endpoint the Credit Checker's Credit Brief section uses
 * (`/case/<id>/credit-brief/`), just given the whole tab area so the PDF
 * renders large enough for a senior credit reviewer to actually read.
 * Auto-loads on mount; the toolbar carries Regenerate + Download.
 */
class CreditBriefTab extends React.Component {
    state = {
        loading: false,
        url: null,
        error: null
    };

    componentDidMount() {
        this.loadBrief(false);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.caseId !== this.props.caseId) {
            this.setState({ url: null, error: null });
            this.loadBrief(false);
        }
    }

    loadBrief = (regenerate = false) => {
        const { caseId } = this.props;
        if (!caseId || this.state.loading) return;
        this.setState({ loading: true, error: null });
        generateCreditBrief(caseId, regenerate)
            .then(res => {
                const url =
                    res && res.data && res.data.data && res.data.data.url;
                if (!url) {
                    this.setState({ error: "Credit brief not available" });
                    message.error("Credit brief not available", 3);
                    return;
                }
                // The PDF is stored at a fixed S3 key. Append a cache-bust
                // param so the iframe always fetches the latest render
                // after a regenerate rather than serving the browser's
                // cached copy from a previous view. A presigned S3 URL is
                // already unique per request, and any extra query param
                // breaks its signature (403 SignatureDoesNotMatch).
                const bust =
                    url.indexOf("X-Amz-Signature=") !== -1
                        ? ""
                        : (url.indexOf("?") === -1 ? "?" : "&") + "t=" + Date.now();
                this.setState({ url: url + bust });
            })
            .catch(() => {
                this.setState({ error: "Failed to generate credit brief" });
                message.error("Failed to generate credit brief", 3);
            })
            .finally(() => this.setState({ loading: false }));
    };

    // Regenerating the PDF (once one already exists) is only actionable at
    // the credit stages — same gate the Credit Checker page's Credit Brief
    // section and TVR reset use. The first-ever "Generate" is left alone.
    canRegenerate = () => [7, 8].includes(Number(this.props.stageId));

    render() {
        const { caseId } = this.props;
        const { loading, url, error } = this.state;
        const regenerateBlocked = Boolean(url) && !this.canRegenerate();
        return (
            <div className={"cb-tab"}>
                <div className={"cb-tab__bar"}>
                    <div className={"cb-tab__title"}>
                        <Icon type={"read"} /> Credit Brief
                    </div>
                    <div className={"cb-tab__actions"}>
                        {url ? (
                            <a
                                href={url}
                                download={`Credit Summary - ${caseId}.pdf`}
                                target={"_blank"}
                                rel={"noreferrer"}
                                className={"cb-tab__btn cb-tab__btn--ghost"}
                            >
                                <Icon type={"download"} /> Download
                            </a>
                        ) : null}
                        <button
                            type={"button"}
                            className={"cb-tab__btn"}
                            onClick={() => this.loadBrief(true)}
                            disabled={loading || regenerateBlocked}
                            title={
                                regenerateBlocked
                                    ? "The Credit Brief can only be regenerated at the credit stage"
                                    : "Re-render the Credit Brief PDF"
                            }
                        >
                            <Icon type={loading ? "loading" : "sync"} />
                            {loading
                                ? "Generating…"
                                : url
                                    ? "Regenerate"
                                    : "Generate"}
                        </button>
                    </div>
                </div>
                <div className={"cb-tab__body"}>
                    {url ? (
                        <iframe
                            title={"credit-brief-pdf"}
                            className={"cb-tab__frame"}
                            src={url}
                        />
                    ) : (
                        <Empty
                            className={"cb-tab__empty"}
                            description={
                                loading
                                    ? "Rendering the credit brief…"
                                    : error ||
                                      "Click “Generate” to render the credit brief"
                            }
                        />
                    )}
                </div>
            </div>
        );
    }
}

CreditBriefTab.propTypes = {
    caseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired,
    // Gates "Regenerate" to the credit stages (7, 8).
    stageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default CreditBriefTab;
